import React, { FC, useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'react-use'

import { ScoreConfig } from '../../../../model/Questionnaire'

import { GridElement, GridLayout, Input, TagSearchAndDisplay } from '../../../shared'
import { ScoreConfigForm } from './ScoreConfigForm'

import { QuestionnaireFormProps } from './QuestionnaireForm.model'
import styles from './QuestionnaireForm.module.scss'
import RoleRestriction from '../../../../containers/RoleRestriction'
import { ADMIN_ROLES, Roles } from '../../../../model/Roles'
import { LabeledCheckbox } from '../../..//legacy/LabeledCheckbox'
import { useCreateTag, useGetTags } from '../../../../hooks/queries/tag'

export const QuestionnaireForm: FC<QuestionnaireFormProps> = ({
  questionnaireTemplate,
  scoreCodes,
  onUpdate,
}) => {
  const [title, setTitle] = useState<string>(questionnaireTemplate.title)
  const [isPrivate, setIsPrivate] = useState<boolean>(questionnaireTemplate.private)
  const [isScore, setIsScore] = useState(!!questionnaireTemplate.scoreConfig)
  const { tagList: tags } = useGetTags()
  const { mutate: createTag } = useCreateTag()

  useDebounce(
    () => {
      onUpdate(questionnaireTemplate.id, {
        title,
        private: isPrivate,
      })
    },
    500,
    [isPrivate, title],
  )

  useEffect(() => {
    if (questionnaireTemplate.scoreConfig && !isScore) {
      onUpdate(questionnaireTemplate.id, {
        scoreConfig: null,
      })
    }
  }, [
    isScore,
    isPrivate,
    onUpdate,
    questionnaireTemplate.id,
    questionnaireTemplate.scoreConfig,
    title,
  ])

  const handleSaveScoreConfig = (scoreConfig: ScoreConfig) => {
    onUpdate(questionnaireTemplate.id, {
      title,
      private: isPrivate,
      scoreConfig,
    })
  }

  const handleTagsUpdate = useCallback(
    (valueIds: string[]) => {
      onUpdate(questionnaireTemplate.id, { tagIds: valueIds })
    },
    [onUpdate, questionnaireTemplate.id],
  )

  const handleTagCreate = useCallback(
    (label: string) => {
      createTag(
        { label },
        { onSuccess: (tag) => handleTagsUpdate([...questionnaireTemplate.tagIds, tag.id]) },
      )
    },
    [createTag, handleTagsUpdate, questionnaireTemplate.tagIds],
  )

  return (
    <form className={styles.form}>
      <GridLayout
        columns={2}
        rowsTemplate="repeat(2, auto)"
        alignColumns="end"
        gap="medium"
        padding="medium"
      >
        <Input
          name="title"
          label="Nom du questionnaire"
          placeholder="Nom du questionnaire"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          disabled={!questionnaireTemplate.isEditable}
          valid={true}
          colorPreset="dark"
        />

        <RoleRestriction roles={[...ADMIN_ROLES, Roles.EXT_DSM]}>
          <LabeledCheckbox
            colorPreset="light"
            checked={isPrivate}
            label="Questionnaire privé"
            onChange={(e) => setIsPrivate(e.target.checked)}
            testId="checkbox-questionnaire_template-is_private"
          />
        </RoleRestriction>
        <GridElement>
          <TagSearchAndDisplay
            colorPreset="dark"
            tags={tags}
            valueIds={questionnaireTemplate.tagIds}
            onChange={handleTagsUpdate}
            onCreate={handleTagCreate}
            placeholder="Rechercher un label"
            disabled={!questionnaireTemplate.isEditable}
            testId="questionnaire"
          />
        </GridElement>
        <GridElement>
          <RoleRestriction roles={[...ADMIN_ROLES, Roles.EXT_DSM]}>
            <LabeledCheckbox
              colorPreset="light"
              checked={isScore}
              label="Score"
              onChange={(e) => setIsScore(e.target.checked)}
              disabled={!questionnaireTemplate.isEditable}
              testId="checkbox-questionnaire_template-is_score"
            />
          </RoleRestriction>
          {isScore && (
            <ScoreConfigForm
              config={questionnaireTemplate.scoreConfig || undefined}
              scoreCodes={scoreCodes}
              disabled={!questionnaireTemplate.isEditable}
              saveScoreConfig={handleSaveScoreConfig}
            />
          )}
        </GridElement>
      </GridLayout>
    </form>
  )
}
